import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { GlossaryAuthor } from "@/components/site/GlossaryAuthor";
import { glossaryHub, glossaryCategories, glossaryTerms, type GlossaryCategory } from "@/data/glossary";
import { SITE_URL, ORG_NAME, jsonLdScript } from "@/lib/seo";

const HUB_URL = `${SITE_URL}/glossary`;

export const Route = createFileRoute("/glossary/")({
  validateSearch: (s: Record<string, unknown>): { category?: string } =>
    typeof s.category === "string" && (glossaryCategories as string[]).includes(s.category) ? { category: s.category } : {},
  head: () => applyPageMetadata("/glossary", {
    meta: [
      { title: glossaryHub.metaTitle },
      { name: "description", content: glossaryHub.metaDescription },
      { property: "og:title", content: glossaryHub.metaTitle },
      { property: "og:description", content: glossaryHub.metaDescription },
      { property: "og:url", content: HUB_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: glossaryHub.metaTitle },
      { name: "twitter:description", content: glossaryHub.metaDescription },
    ],
    links: [{ rel: "canonical", href: HUB_URL }],
    scripts: [
      jsonLdScript({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "DefinedTermSet",
            "@id": `${HUB_URL}#termset`,
            name: "Law Firm SEO Glossary",
            description: glossaryHub.metaDescription,
            url: HUB_URL,
            publisher: { "@type": "Organization", name: ORG_NAME, url: `${SITE_URL}/` },
            hasDefinedTerm: glossaryTerms.map((t) => ({ "@type": "DefinedTerm", name: t.term, url: `${HUB_URL}/${t.slug}` })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Glossary", item: HUB_URL },
            ],
          },
        ],
      }),
    ],
  }),
  component: GlossaryHub,
});

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const firstSentence = (s: string) => (s.match(/^.*?[.!?](\s|$)/)?.[0] ?? s).trim();
const letterOf = (t: string) => {
  const c = t.trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : "#";
};

function GlossaryHub() {
  const { category: initialCat } = Route.useSearch();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<GlossaryCategory | "All">((initialCat as GlossaryCategory) ?? "All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return glossaryTerms
      .filter((t) => (cat === "All" || t.category === cat) && (!q || t.term.toLowerCase().includes(q)))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [query, cat]);

  const groups = useMemo(() => {
    const m = new Map<string, typeof filtered>();
    for (const t of filtered) {
      const l = letterOf(t.term);
      m.set(l, [...(m.get(l) ?? []), t]);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);
  const available = new Set(groups.map(([l]) => l));

  const reset = () => { setQuery(""); setCat("All"); };

  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 pt-10 pb-24 sm:px-6 sm:pt-14 sm:pb-28 grid gap-8 lg:grid-cols-[1fr_340px] lg:items-end">
          <div className="max-w-3xl">
            <nav aria-label="Breadcrumb" className="mb-5 text-xs text-primary-foreground/70">
              <ol className="flex items-center gap-1">
                <li><a href="/" className="hover:text-[var(--gold)]">Home</a></li>
                <li>/</li>
                <li className="text-primary-foreground">Glossary</li>
              </ol>
            </nav>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold mb-5">{glossaryHub.h1}</h1><LastUpdated />
            <p className="text-primary-foreground/80 text-lg leading-relaxed">{glossaryHub.intro}</p>
          </div>
          <GlossaryAuthor dark />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 -mt-14 relative z-10">
        <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-[var(--shadow-elegant)]">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full sm:max-w-md">
              <label htmlFor="glossary-search" className="block text-sm font-semibold text-foreground mb-1.5">Search terms</label>
              <input id="glossary-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Local Pack"
                className="w-full rounded-md border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]" />
            </div>
            <p className="text-sm text-muted-foreground" aria-live="polite">Showing {filtered.length} of {glossaryTerms.length} terms</p>
          </div>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap">
            {(["All", ...glossaryCategories] as const).map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)} aria-pressed={cat === c}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${cat === c ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/80 border-border hover:border-primary/40"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-1 border-t border-border pt-4">
            {LETTERS.map((l) =>
              available.has(l) ? (
                <a key={l} href={`#letter-${l}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold text-primary hover:bg-muted">{l}</a>
              ) : (
                <span key={l} aria-disabled="true" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm text-muted-foreground/40">{l}</span>
              ),
            )}
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-6 sm:py-16">
        {groups.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-foreground mb-4">No terms match your search in this category.</p>
            <button type="button" onClick={reset} className="rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold">Show all terms</button>
          </div>
        ) : (
          <div className="space-y-12">
            {groups.map(([letter, terms]) => (
              <div key={letter} id={`letter-${letter}`} className="scroll-mt-28 grid gap-6 md:grid-cols-[80px_1fr]">
                <div className="font-display text-5xl font-semibold text-[var(--gold-deep)] leading-none">{letter}</div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {terms.map((t) => (
                    <Link key={t.slug} to="/glossary/$slug" params={{ slug: t.slug }}
                      className="group block rounded-xl border border-border bg-card p-5 shadow-[var(--shadow-card)] hover:border-[var(--gold)] hover:-translate-y-0.5 transition">
                      <span className="block font-display text-xl font-semibold text-foreground group-hover:text-primary">{t.term}</span>
                      <span className="mt-1 block text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">{t.category}</span>
                      <span className="mt-3 block text-sm text-muted-foreground line-clamp-3">{firstSentence(t.shortDefinition)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-5">See how these terms apply to your firm</h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8">Get a free audit that shows where your site stands on local rankings, technical health, content and AI search.</p>
          <div className="flex flex-col gap-3 justify-center sm:flex-row">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Book a call</CTAButton>
          </div>
        </div>
      </section>
    </Layout>
  );
}
